import styled from '@emotion/styled';
import { i18n, usePaymentSlip } from 'src/common';
import { useGetGeneralTransferDetails } from 'src/common/api/apollo/queries';
import { BarCode, Button, ContainerComponent, Flex, Loader, Text } from 'src/components';
import { colors } from 'src/theme';

const CardContainerUp = styled(Flex)({
  padding: '8px 16px',
  backgroundColor: colors.primary1,
  borderRadius: '12px 12px 0 0',
});

const CardContainerDown = styled(Flex)({
  padding: '8px 16px',
  borderRadius: '12px',
});

const FlexCard = styled(Flex)({
  padding: '4px 8px',
  borderRadius: '4px',
  backgroundColor: colors.background6,
  width: '100%',
});

const FlexStyle = styled(Flex)({
  width: '100%',
});

const Card = styled.div({
  borderRadius: 12,
  overflow: 'hidden',
  border: `1px solid ${colors.border6}`,
  backgroundColor: colors.white,
});

const CheckoutPaymentSentPage = () => {
  const { data, loading: loadingDetails } = useGetGeneralTransferDetails(1);
  const { downloadSlip, loading: loadingSlip, PaymentSlip } = usePaymentSlip('oneTime');

  if (loadingDetails || loadingSlip || !data) return <Loader page />;

  const referenceValue = `${data.referencePrefix ?? ''}${data.referenceNumberType === 'oib' ? (data.referenceNumber ?? '') : ''}${data.referenceSuffix ?? ''}`;

  const paymentDetails = [
    {
      label: i18n.t('pages.checkoutPaymentSentPage.payer'),
      value: `${data.customerFirstName ?? ''} ${data.customerLastName ?? ''}`.trim(),
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.receiver'),
      value: (
        <Flex vertical align="flex-end">
          <Text type="small" color={colors.text1}>{data.recipientName}</Text>
          <Text type="small" color={colors.text1}>{data.recipientAddress}</Text>
          <Text type="small" color={colors.text1}>{`${data.recipientZipCode ?? ''} ${data.recipientCity ?? ''}`.trim()}</Text>
        </Flex>
      ),
      isNode: true,
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.amount'),
      value: data.price != null ? `${data.price.toFixed(2)}€` : '',
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.referenceNumber'),
      value: referenceValue,
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.model'),
      value: data.model ?? '',
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.description'),
      value: data.description ?? '',
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.iban'),
      value: data.recipientAccountNumber ?? '',
    },
  ];

  return (
    <ContainerComponent>
        <Flex vertical gap={8}>
          <Text type="xxxLarge" bold>{i18n.t('pages.checkoutPaymentSentPage.payment')}</Text>
          <Text type="base">{i18n.t('pages.checkoutPaymentSentPage.paymentSubtitle')}</Text>
        </Flex>
        <Card>
          <CardContainerUp justify="space-between">
            <Text type="base" color={colors.white}>
              {i18n.t('pages.checkoutPaymentSentPage.toPay')}
            </Text>
            <Text type="base" color={colors.white} bold>
              {data.price != null ? `${data.price.toFixed(2)}€` : ''}
            </Text>
          </CardContainerUp>
          <CardContainerDown vertical gap={24}>
            <Flex vertical gap={8} align="center">
              <Text type="small" color={colors.text1}>
                {i18n.t('pages.checkoutPaymentSentPage.barcodePayment')}
              </Text>
              <BarCode
                price={data.price != null ? data.price.toFixed(2).replace('.', ',') : '0,00'}
                userFirstName={data.customerFirstName ?? ''}
                userLastName={data.customerLastName ?? ''}
                userAddress={data.customerAddress ?? ''}
                userCity={data.customerCity ?? ''}
                oib={data.customerOib ?? '0'}
                recipient={data.recipientName ?? ''}
                recipientAddress={data.recipientAddress ?? ''}
                recipientCity={data.recipientCity ?? ''}
                model={data.model ?? ''}
                iban={data.recipientAccountNumber ?? ''}
                description={data.description ?? ''}
                hidden={false}
              />
            </Flex>
            <Flex vertical gap={8} align="center">
              <Text type="small" color={colors.text1}>
                {i18n.t('pages.checkoutPaymentSentPage.or')}
              </Text>
            </Flex>
            <Flex vertical gap={8} align="center">
              <PaymentSlip />
              <Text type="small">{i18n.t('pages.checkoutPaymentSentPage.downloadSlip')}</Text>
              <FlexStyle vertical gap={2} align="center">
                {paymentDetails.map((detail, index) => (
                  <FlexCard key={index} gap={16} align="center" justify="space-between">
                    <Text type="tiny" color={colors.text4}>{detail.label}</Text>
                    {detail.isNode ? (
                      detail.value
                    ) : (
                      <Text type="small" color={colors.text1}>{detail.value as string}</Text>
                    )}
                  </FlexCard>
                ))}
              </FlexStyle>
              <Button
                btnType="outlinePrimary"
                size="large"
                onClick={() => downloadSlip()}
                style={{ width: '100%' }}
              >
                {i18n.t('pages.checkoutPaymentSentPage.downloadSlipButton')}
              </Button>
            </Flex>
          </CardContainerDown>
        </Card>
    </ContainerComponent>
  );
};

export default CheckoutPaymentSentPage;
